import { DOCUMENT } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  WritableSignal
} from '@angular/core';
import { Account as AccountService } from '@customer/account/account.svc';
import { AuthService, PermissionsService } from '@customer/services';
import { LightboxService, StorageService, ToastService } from '@core/services';
import { Switcher as SwitcherComponent } from '@customer/account/switcher/switcher';
import { RouterLink, RouterLinkActive } from '@angular/router';
import posthog from 'posthog-js';
import { SessionAuth } from '@legacy-models/auth/user';
import { Account } from '@legacy-models/portal/Account';
import { first } from 'rxjs';

@Component({
  selector: 'oke-sidebar',
  imports: [RouterLinkActive, RouterLink],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar implements OnInit, OnDestroy {
  readonly authSvc = inject(AuthService);
  readonly permissionsSvc = inject(PermissionsService);
  readonly accountSvc = inject(AccountService);
  private readonly _lightboxSvc = inject(LightboxService);
  private readonly _document = inject(DOCUMENT);
  readonly storageSvc = inject(StorageService);
  private readonly _toastSvc = inject(ToastService);
  readonly $modalContent = viewChild.required<TemplateRef<unknown>>('content');
  user: SessionAuth | null = null;
  acct: Account | null = null;
  accounts: Account[] = [];
  private readonly _$isLoading: WritableSignal<boolean> = signal<boolean>(true);
  protected readonly $isLoading: Signal<boolean> = this._$isLoading.asReadonly();
  isUserNGP = false;
  isUserNGGP = false;

  ngOnInit(): void {
    //subscribe from current user and account observable to track current user state
    this.storageSvc.currentUser$.subscribe(currUser => {
      if (currUser) {
        this.user = currUser;
        this.isUserNGGP =
          currUser.Contact?.BusinessSegments?.some((businessSegment: string) => {
            return businessSegment.toLowerCase() === 'nggp';
          }) ?? false;
        this.isUserNGP =
          currUser.Contact?.BusinessSegments?.some((businessSegment: string) => {
            return ['ngpintrastate'].some((ngpSegment: string) => {
              return businessSegment.toLowerCase() === ngpSegment;
            });
          }) ?? false;
      }
    });
    this.storageSvc.currentAccount$.subscribe(currAcc => {
      if (currAcc) {
        this.acct = currAcc;
      }
    });
    this.accountSvc.getAccounts().subscribe(
      (accts: Account[]) => {
        if (accts && accts.length) {
          this.accounts = this.setAccountsBasedOnSegment(accts);
          //sorting accounts alphabetically
          this.accounts.sort((a, b) => a.AccountName.toUpperCase().localeCompare(b.AccountName.toUpperCase()));
          if (!this.user?.HasAccountSelected) {
            this._lightboxSvc.openComponent(SwitcherComponent, {
              inputs: {
                accounts: this.accounts
              }
            });
          }
        } else {
          this._handleErrorAccounts(false);
        }
      },
      () => this._handleErrorAccounts(),
      () => {
        this._$isLoading.set(false);
      }
    );
  }

  private _handleErrorAccounts(isNoData = false): void {
    setTimeout(() => {
      if (isNoData) {
        this._toastSvc.alert('Company Access', 'There are no companies associated with your user.', {
          shouldShowHelpCenter: true,
          shouldShowServiceDesk: false,
          animation: 'slide-from-end'
        });
      } else {
        this._toastSvc.alert('Company Access', "There was an issue connecting to your company's account.", {
          shouldShowServiceDesk: true,
          animation: 'slide-from-end'
        });
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.storageSvc.myAccountsSource.next(null);
    this.storageSvc.currentAccountSource.next(null);
    this.storageSvc.myAccountsSource.next(null);
    this.storageSvc.clearAppStorage();
  }

  isValidSegmentPage(isCurrentPage: boolean, segmentAbbreviation?: string): boolean {
    let isValid = false;
    if (isCurrentPage) {
      isValid = true;
    } else if (typeof segmentAbbreviation !== 'undefined' && !!this.storageSvc.currentPageSource.value) {
      let isSubPage = false;
      if (segmentAbbreviation === 'nggp') {
        isSubPage = [
          'measurement',
          'volume',
          'settlement statements',
          'check details',
          'nggp requests',
          'documents',
          'outages',
          'invoices'
        ].some(feature => this.storageSvc.currentPageSource.value!.toLowerCase().includes(feature));
      } else if (segmentAbbreviation === 'ngp') {
        isSubPage = ['ngp requests'].some(feature =>
          this.storageSvc.currentPageSource.value!.toLowerCase().includes(feature)
        );
      }
      if (isSubPage) {
        isValid = true;
      }
    }
    return isValid;
  }

  onLogout(): void {
    const lightbox = this._lightboxSvc.openConfirmation({
      title: 'Are you sure?',
      body: '<p>Do you want to <strong>sign out?</strong>',
      positiveLabel: 'sign out',
      negativeLabel: 'not yet'
    });
    lightbox.closed$.pipe(first()).subscribe(isPositive => {
      if (isPositive) {
        this.authSvc.logoutUser();
      }
    });
  }

  switchAccount(): void {
    this._lightboxSvc.openComponent(SwitcherComponent, {
      inputs: {
        accounts: this.accounts
      }
    });
    posthog.capture('app:switch_account_button_click');
  }

  setAccountsBasedOnSegment(accounts: Account[]): Account[] {
    const nggpAccts: Account[] = [];
    const ngpIntrastateAccts: Account[] = [];
    let sessionAccts: Account[] = [];

    if (this.permissionsSvc.hasPortalAdminRole() || (this.isUserNGP && this.isUserNGGP)) {
      sessionAccts = sessionAccts.concat(accounts);
      return sessionAccts;
    }

    accounts.forEach(acct => {
      if (acct.BusinessSegments?.includes('NGGP')) {
        nggpAccts.push(acct);
      }
      if (acct.BusinessSegments?.includes('NGPIntrastate')) {
        ngpIntrastateAccts.push(acct);
      }
    });

    if (this.isUserNGP) {
      sessionAccts = sessionAccts.concat(ngpIntrastateAccts);
    }
    if (this.isUserNGGP) {
      sessionAccts = sessionAccts.concat(nggpAccts);
    }

    if (!this.isUserNGP && !this.isUserNGGP) {
      sessionAccts = accounts;
    }

    return sessionAccts;
  }

  hideSidebarMenu(): void {
    this._document.body.classList.remove('sidebar--engaged');
    this._document.getElementById('app-header-sidebar-btn-icon')!.dispatchEvent(new Event('click'));
  }

  isNGPTabVisible(): boolean {
    let isVisible = false;
    const hasSegment = this.permissionsSvc.hasNGPBusinessSegment();
    const hasNGPRole = this.permissionsSvc.hasNGPUserRole();
    let hasNGPSegment = false;
    if (this.acct) {
      hasNGPSegment =
        (this.acct.BusinessSegments?.length ?? 0) === 0 ||
        this.acct.BusinessSegments?.includes('NGPIntrastate') === true;
    }

    if (hasSegment && hasNGPRole && hasNGPSegment) {
      isVisible = true;
    }

    return isVisible;
  }

  isNGGPTabVisible(): boolean {
    let isVisible = false;
    const hasSegment = this.permissionsSvc.hasNGGPBusinessSegment();
    const hasNGGPRole = this.permissionsSvc.hasAccountAccessRole();
    let hasNGGPSegment = false;
    if (this.acct) {
      hasNGGPSegment =
        (this.acct.BusinessSegments?.length ?? 0) === 0 || this.acct.BusinessSegments?.includes('NGGP') === true;
    }

    if (hasSegment && hasNGGPRole && hasNGGPSegment) {
      isVisible = true;
    }

    return isVisible;
  }

  isAdministrationTabVisible(): boolean {
    let isVisible = false;
    const hasExternalAdminRole = this.permissionsSvc.hasExternalAdminRole();
    const isONEOKEmployee = this.permissionsSvc.hasPortalEmployeeRole();
    const isMeasurmentEmployee = this.permissionsSvc.hasMeasurementEmployeeRole();

    if (hasExternalAdminRole || isONEOKEmployee || isMeasurmentEmployee) {
      isVisible = true;
    }
    return isVisible;
  }
}
